import { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { cx } from '@linaria/atomic';
import type { CommonHTMLAttributes } from '@frontend/types';
import { getFontColorClassName } from '../../helpers/font-color.linaria';
import type { FontColorType } from '../../helpers/font-styles';
import { splitStylesProp, type StylesProp } from '../../helpers/styles-prop';
import { getTextAlignClassName } from '../../helpers/text-align.linaria';
import {
  getTextSizeClassName,
  getTextWeightClassName,
  textBaseStyle,
  type FontWeight,
  type TextAlignment,
  type TextSize,
} from './text-style-helpers';

type DragProps = Pick<HTMLAttributes<HTMLElement>, 'draggable' | 'onDragStart' | 'onDragEnd'>;

export type TextProps = CommonHTMLAttributes & {
  color?: FontColorType;
  as?: ElementType;
  size?: TextSize;
  textAlign?: TextAlignment;
  weight?: FontWeight;
  css?: StylesProp;
} & DragProps;

export const Text = forwardRef<HTMLElement, TextProps>(
  (
    { size = 'large', weight = 'inherit', color = 'default', textAlign, as = 'p', className, css, children, ...props },
    ref
  ) => {
    const Component = as;
    const { css: textCss, className: textClassName } = splitStylesProp(css);
    return (
      <Component
        ref={ref}
        className={cx(
          textBaseStyle,
          getTextSizeClassName(size),
          getTextWeightClassName(weight),
          getFontColorClassName(color),
          textAlign && getTextAlignClassName(textAlign),
          className,
          textClassName
        )}
        css={textCss}
        {...props}
      >
        <>{children}</>
      </Component>
    );
  }
);

Text.displayName = 'Text';
